import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ListSourceType } from "@/enum/list.enum";
import { Order } from "@/types/types";

import { useGetLeanListsQuery, useLazyGetLeanListsQuery } from "../../../redux/list/list.api";
import { List } from "../../List/schema/list";

const LIST_PAGE_SIZE = 15;
/** Bigger pages while enumerating a whole match set — that path is a burst, not a scroll. */
const SELECT_ALL_PAGE_SIZE = 100;
/** Backstop so a bad `pageCount` can never spin the loop forever. */
const SELECT_ALL_MAX_PAGES = 20;
const SEARCH_DEBOUNCE_MS = 400;

/**
 * The Overview's "show only lists that…" quick filters, mirrored onto the lean endpoint's
 * `hasLeads` / `hasCampaigns` params. Both may be on at once, which the API reads as AND.
 */
export interface AnalyticsListContentFilter {
	hasLeads: boolean;
	hasCampaigns: boolean;
}

export type AnalyticsListContentFilterKey = keyof AnalyticsListContentFilter;

export const NO_LIST_CONTENT_FILTER: AnalyticsListContentFilter = {
	hasLeads: false,
	hasCampaigns: false,
};

export interface UseAnalyticsListOptionsOptions {
	/** Restricts the options to one list source — the Signals tab passes `TRACKER`. */
	sourceType?: ListSourceType;
}

export interface AnalyticsListOptions {
	/** Every page loaded so far, in order. */
	lists: List[];
	isFetching: boolean;
	hasMore: boolean;
	searchValue: string;
	onSearch: (value: string) => void;
	onLoadMore: () => void;
	contentFilter: AnalyticsListContentFilter;
	toggleContentFilter: (key: AnalyticsListContentFilterKey) => void;
	/** Every list matching a content filter, across all pages. Used by the quick filters. */
	fetchAllMatching: (filter: AnalyticsListContentFilter) => Promise<List[]>;
	isFetchingAll: boolean;
}

/** Whether a freshly-arrived page is the one already stored, compared by list id. */
const isSamePage = (stored: List[] | undefined, incoming: List[]): boolean =>
	!!stored &&
	stored.length === incoming.length &&
	stored.every((list, index) => list.id === incoming[index].id);

/** The identity of a result set — everything except which page of it we are on. */
const scopeKeyOf = (
	sourceType: ListSourceType | undefined,
	searchValue: string,
	filter: AnalyticsListContentFilter
): string => `${sourceType ?? ""}|${searchValue}|${filter.hasLeads}|${filter.hasCampaigns}`;

const queryParamsFor = (
	sourceType: ListSourceType | undefined,
	searchValue: string,
	filter: AnalyticsListContentFilter
) => ({
	searchValue,
	sorting: { sortBy: "createdAt", sortOrder: Order.DESC },
	// The pill turns a picked list into its campaign chips, and the steps modal reads the
	// saved sequence off the same object — both are empty without the config.
	withCampaignConfig: true,
	...(sourceType ? { sourceType } : {}),
	...(filter.hasLeads ? { hasLeads: true } : {}),
	...(filter.hasCampaigns ? { hasCampaigns: true } : {}),
});

/**
 * The options behind the Analytics List filter: the pages loaded so far, the search and
 * "load more" that reach the rest, and the two content quick filters.
 *
 * Owns the lean-list query for the whole page. Pages accumulate so scrolling the popover
 * never drops what is already visible, and the accumulator is keyed by the *scope* — a new
 * search or quick filter starts a fresh set rather than appending to a result set the user
 * can no longer see.
 */
export function useAnalyticsListOptions({
	sourceType,
}: UseAnalyticsListOptionsOptions = {}): AnalyticsListOptions {
	const [page, setPage] = useState(1);
	const [searchValue, setSearchValue] = useState("");
	const [contentFilter, setContentFilter] =
		useState<AnalyticsListContentFilter>(NO_LIST_CONTENT_FILTER);
	const [pages, setPages] = useState<List[][]>([]);

	const scopeKey = scopeKeyOf(sourceType, searchValue, contentFilter);
	const scopeKeyRef = useRef(scopeKey);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// `currentData` (not `data`) is undefined while a *different* arg is in flight, so a
	// page from the previous scope can never be filed under the new one.
	const { currentData, isFetching } = useGetLeanListsQuery({
		page,
		limit: LIST_PAGE_SIZE,
		...queryParamsFor(sourceType, searchValue, contentFilter),
	});

	const [triggerLeanLists, { isFetching: isFetchingAll }] = useLazyGetLeanListsQuery();

	// Reset before the new scope's first page lands, so the popover never shows the old
	// set mixed with the new one.
	if (scopeKeyRef.current !== scopeKey) {
		scopeKeyRef.current = scopeKey;

		if (page !== 1) {
			setPage(1);
		}

		if (pages.length > 0) {
			setPages([]);
		}
	}

	useEffect(() => {
		if (!currentData) {
			return;
		}

		setPages((prev) => {
			// Bail out when the slot already holds this page. The updater runs on every new
			// response *reference*, and returning a fresh array unconditionally would make
			// each render produce the next one — an unbreakable loop.
			if (isSamePage(prev[page - 1], currentData.data)) {
				return prev;
			}

			const next = [...prev];

			next[page - 1] = currentData.data;

			return next;
		});
	}, [currentData, page]);

	useEffect(
		() => () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		},
		[]
	);

	const lists = useMemo(() => pages.flat().filter(Boolean), [pages]);
	const pageCount = currentData?.meta?.pageCount ?? 0;

	const onSearch = useCallback((value: string) => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = setTimeout(() => setSearchValue(value), SEARCH_DEBOUNCE_MS);
	}, []);

	const onLoadMore = useCallback(() => {
		if (!isFetching && page < pageCount) {
			setPage((current) => current + 1);
		}
	}, [isFetching, page, pageCount]);

	const toggleContentFilter = useCallback((key: AnalyticsListContentFilterKey) => {
		setContentFilter((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	/**
	 * Pages through the whole match set. The quick filters both narrow the options *and*
	 * select what they match, and selecting only the first page would silently scope a
	 * report to 15 arbitrary lists.
	 */
	const fetchAllMatching = useCallback(
		async (filter: AnalyticsListContentFilter): Promise<List[]> => {
			const collected: List[] = [];

			for (let current = 1; current <= SELECT_ALL_MAX_PAGES; current += 1) {
				const response = await triggerLeanLists({
					page: current,
					limit: SELECT_ALL_PAGE_SIZE,
					...queryParamsFor(sourceType, searchValue, filter),
				}).unwrap();

				collected.push(...response.data);

				if (current >= (response.meta?.pageCount ?? 0)) {
					break;
				}
			}

			return collected;
		},
		[triggerLeanLists, sourceType, searchValue]
	);

	return {
		lists,
		isFetching,
		hasMore: page < pageCount,
		searchValue,
		onSearch,
		onLoadMore,
		contentFilter,
		toggleContentFilter,
		fetchAllMatching,
		isFetchingAll,
	};
}
